/**
 * Users Service - API для управления пользователями
 */

const API_BASE = '/api/users';

/**
 * Получить список пользователей
 */
export async function getUsers({ page = 1, pageSize = 25, role = null, excludeRole = null, isActive = null, search = null } = {}) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('page_size', pageSize);
  if (role) params.append('role', role);
  if (excludeRole) params.append('exclude_role', excludeRole);
  if (isActive !== null) params.append('is_active', isActive);
  if (search) params.append('search', search);

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch users');
  }

  return response.json();
}

/**
 * Получить пользователя по ID
 */
export async function getUser(userId) {
  const response = await fetch(`${API_BASE}/${userId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch user');
  }

  return response.json();
}

/**
 * Создать пользователя
 */
export async function createUser(data) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create user');
  }

  return response.json();
}

/**
 * Обновить пользователя
 */
export async function updateUser(userId, data) {
  const response = await fetch(`${API_BASE}/${userId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update user');
  }

  return response.json();
}

/**
 * Сбросить пароль пользователя
 */
export async function resetUserPassword(userId, newPassword, requireChange = true) {
  const response = await fetch(`${API_BASE}/${userId}/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      new_password: newPassword,
      require_change: requireChange,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reset password');
  }

  return response.json();
}

/**
 * Деактивировать пользователя
 */
export async function deactivateUser(userId) {
  const response = await fetch(`${API_BASE}/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to deactivate user');
  }

  return response.json();
}

/**
 * Активировать пользователя
 */
export async function activateUser(userId) {
  const response = await fetch(`${API_BASE}/${userId}/activate`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to activate user');
  }

  return response.json();
}

/**
 * Получить список ролей
 */
export async function getRoles() {
  const response = await fetch(`${API_BASE}/roles`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch roles');
  }

  return response.json();
}

/**
 * Получить историю аудита пользователя
 */
export async function getUserAuditLog(userId, page = 1, pageSize = 25) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('page_size', pageSize);

  const response = await fetch(`${API_BASE}/${userId}/audit?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch audit log');
  }

  return response.json();
}
